import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import API from "../../api/axiosConfig";
import { ArrowLeft, ClipboardList, KeyRound, Printer, ShoppingBag } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FOOD_KEYWORDS } from "../../utils/isRestaurantCategory";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

const ACTIVE_STATUSES  = ["pendiente", "confirmado", "asignado", "en_camino"];
const HISTORY_STATUSES = ["entregado", "cancelado"];

const STATUS_LABELS = {
  pendiente:  "Pendiente",
  confirmado: "Confirmado",
  asignado:   "Asignado",
  en_camino:  "En camino",
  entregado:  "Entregado",
  cancelado:  "Cancelado",
};

const STATUS_COLORS = {
  pendiente:  "bg-amber-100 text-amber-800 border-amber-200",
  confirmado: "bg-blue-100 text-blue-800 border-blue-200",
  asignado:   "bg-yellow-100 text-yellow-800 border-yellow-200",
  en_camino:  "bg-purple-100 text-purple-800 border-purple-200",
  entregado:  "bg-green-100 text-green-800 border-green-200",
  cancelado:  "bg-red-100 text-red-800 border-red-200",
};

function getCompanyIdFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.companyId ?? u?.company?.id ?? null;
  } catch { return null; }
}

export default function CompanyOrders() {
  const { id: paramId } = useParams();
  const id = paramId || getCompanyIdFromStorage();
  const navigate = useNavigate();

  const [orders,           setOrders]           = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [tab,              setTab]              = useState("active");
  const [subFilter,        setSubFilter]        = useState("all");
  const [pickupCodeEnabled, setPickupCodeEnabled] = useState(false);
  const [togglingCode,     setTogglingCode]     = useState(false);
  const [comandaEnabled,   setComandaEnabled]   = useState(false);
  const [togglingComanda,  setTogglingComanda]  = useState(false);
  const [isRestaurant,     setIsRestaurant]     = useState(false);

  /* Fetch all orders without status filter — filter client-side by tab */
  const loadOrders = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await API.get(`/orders/company/${id}`);
      const all = res.data?.data?.data ?? res.data?.data ?? [];
      // Los pedidos de mesa tienen su propia pantalla dedicada (Mesas) — mezclarlos
      // aquí confundiría la vista de domicilio/recogida.
      setOrders((Array.isArray(all) ? all : []).filter((o) => o.deliveryType !== "mesa"));
    } catch (error) {
      console.error("Error cargando pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrdersRef = useRef(null);
  loadOrdersRef.current = loadOrders;

  useEffect(() => {
    API.get(`/companies/${id}`).then((res) => {
      const c = res.data?.data?.company ?? res.data?.data ?? res.data;
      setPickupCodeEnabled(Boolean(c?.pickupCodeEnabled));
      setComandaEnabled(Boolean(c?.comandaEnabled));
      const catName = (c?.category?.name || "").toLowerCase();
      setIsRestaurant(FOOD_KEYWORDS.some((k) => catName.includes(k)));
    }).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line
  }, [id]);

  /* Socket.io + polling */
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;

    const socket = io(SOCKET_URL, { transports: ["websocket"], auth: { token } });
    const silentRefresh = () => loadOrdersRef.current?.(false);

    socket.on("order:new", (newOrder) => {
      if (newOrder.companyId !== id && newOrder.company?.id !== id) return;
      silentRefresh();
    });
    socket.on("order:status", silentRefresh);

    const interval = setInterval(silentRefresh, 30000);
    return () => { socket.disconnect(); clearInterval(interval); };
    // eslint-disable-next-line
  }, [id]);

  const togglePickupCode = async () => {
    setTogglingCode(true);
    try {
      const newVal = !pickupCodeEnabled;
      await API.patch(`/companies/${id}/pickup-code`, { enabled: newVal });
      setPickupCodeEnabled(newVal);
      toast.success(newVal ? "Código de recogida activado" : "Código de recogida desactivado");
    } catch {
      toast.error("Error al actualizar la configuración");
    } finally {
      setTogglingCode(false);
    }
  };

  const toggleComanda = async () => {
    setTogglingComanda(true);
    try {
      const newVal = !comandaEnabled;
      await API.patch(`/companies/${id}/comanda`, { enabled: newVal });
      setComandaEnabled(newVal);
      toast.success(newVal ? "Comanda de cocina activada" : "Comanda de cocina desactivada");
    } catch {
      toast.error("Error al actualizar la configuración");
    } finally {
      setTogglingComanda(false);
    }
  };

  /* ── Tab / filter logic ─────────────────────────────────────── */
  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSubFilter("all");
  };

  const tabOrders = orders.filter((o) =>
    tab === "active"
      ? ACTIVE_STATUSES.includes(o.status)
      : HISTORY_STATUSES.includes(o.status),
  );

  const filteredOrders = subFilter === "all"
    ? tabOrders
    : tabOrders.filter((o) => o.status === subFilter);

  const pendingCount  = orders.filter((o) => o.status === "pendiente").length;
  const activeCount   = orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;
  const historyCount  = orders.filter((o) => HISTORY_STATUSES.includes(o.status)).length;

  const activeSubFilters = [
    { value: "all",        label: "Todos" },
    { value: "pendiente",  label: "Pendiente"  },
    { value: "confirmado", label: "Confirmado" },
    { value: "asignado",   label: "Asignado"   },
    { value: "en_camino",  label: "En camino"  },
  ];

  const historySubFilters = [
    { value: "all",       label: "Todos"     },
    { value: "entregado", label: "Entregado" },
    { value: "cancelado", label: "Cancelado" },
  ];

  const subFilters = tab === "active" ? activeSubFilters : historySubFilters;

  return (
    <div className="max-w-5xl mx-auto px-6 pt-4 pb-16">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-supernova-600 hover:text-supernova-700 mb-6 font-semibold"
      >
        <ArrowLeft size={20} /> Volver
      </button>

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <ClipboardList className="text-supernova-600" size={32} />
          Pedidos
        </h1>
        <div className="flex items-center gap-2">
          {isRestaurant && (
            <button
              onClick={toggleComanda}
              disabled={togglingComanda}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                comandaEnabled
                  ? "bg-green-500 text-white border-green-500"
                  : "bg-white text-gray-600 border-gray-300"
              }`}
            >
              <Printer size={15} />
              {comandaEnabled ? "Comanda activa" : "Comanda cocina"}
            </button>
          )}
          <button
            onClick={togglePickupCode}
            disabled={togglingCode}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition ${
              pickupCodeEnabled
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-gray-600 border-gray-300"
            }`}
          >
            <KeyRound size={15} />
            {pickupCodeEnabled ? "Código activo" : "Código recogida"}
          </button>
        </div>
      </div>

      {/* ── Main tabs ── */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => handleTabChange("active")}
          className={`relative flex-1 py-2.5 rounded-xl font-semibold transition text-sm ${
            tab === "active"
              ? "bg-supernova-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Activos
          {activeCount > 0 && (
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
              tab === "active" ? "bg-black/20 text-white" : "bg-supernova-100 text-supernova-700"
            }`}>
              {activeCount}
            </span>
          )}
          {pendingCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
          )}
        </button>
        <button
          onClick={() => handleTabChange("history")}
          className={`flex-1 py-2.5 rounded-xl font-semibold transition text-sm ${
            tab === "history"
              ? "bg-supernova-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Historial
          {historyCount > 0 && (
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
              tab === "history" ? "bg-black/20 text-white" : "bg-gray-200 text-gray-600"
            }`}>
              {historyCount}
            </span>
          )}
        </button>
      </div>

      {/* ── Sub-filter chips ── */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
        {subFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setSubFilter(f.value)}
            className={`px-3 py-1.5 rounded-full border whitespace-nowrap text-sm transition ${
              subFilter === f.value
                ? "bg-gray-800 text-white border-gray-800"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Order list ── */}
      {loading ? (
        <p className="text-center text-gray-500 mt-10">Cargando pedidos...</p>
      ) : filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <ShoppingBag size={48} className="mb-3 opacity-40" />
          <p className="font-medium">
            {tab === "active" ? "No hay pedidos activos" : "No hay pedidos en el historial"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((o) => (
            <div
              key={o.id}
              onClick={() => navigate(`/order/${o.id}`)}
              className={`rounded-xl border p-4 cursor-pointer hover:shadow-md transition ${
                o.status === "pendiente"
                  ? "order-pending-blink"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-800">
                      Pedido #{o.orderNumber}
                    </p>
                    {o.status === "pendiente" && (
                      <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full animate-pulse font-semibold">
                        ¡Nuevo!
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLORS[o.status] ?? ""}`}>
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Cliente: <span className="font-medium text-gray-700">{o.user?.name}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(o.createdAt).toLocaleString("es-CO")}
                  </p>
                </div>

                <div className="text-right shrink-0">
                  <p className="font-bold text-supernova-600 text-lg">
                    ${Number(o.total ?? 0).toLocaleString("es-CO")}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
